import Rollbar from 'rollbar';

import {
    LoggerContext, LoggerLevel, LogObject, Transport,
} from 'src/types';

export type createRollbarTransporter = (
    environment: string,
    config: import('rollbar').Configuration,
    global?: Partial<import('rollbar').Configuration>,
) => Transport;

/**
 * Combine the LogObject (omitting the message field) with the provided context
 * object to send as a custom rollbar object
 *
 * @param payload
 * @param context
 */
function formatRollbarCustomObject(
    payload: LogObject,
    context: LoggerContext,
): Omit<LogObject, 'message'> & LoggerContext {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { message, ...rest } = payload;

    return { ...context, ...rest };
}

/**
 * Creates an instance of a Rollbar logger.
 * Configuration is merged from our configuration (appConfig) and any
 * additional parameters provided to the function.
 *
 * When not in production, the rollbar logger will also log to the `console`.
 *
 * @param params (optional) An object containing `notifier` configuration
 *
 * {@see https://docs.rollbar.com/docs/rollbarjs-configuration-reference}
 */
function getRollbarLogger(
    environment: string,
    config: Rollbar.Configuration,
    global?: Partial<Rollbar.Configuration>,
): Rollbar {
    const opts: Rollbar.Configuration = {
        ...config,
        payload: {
            ...config.payload,
            environment,
        },
    };

    const rollbar = new Rollbar(opts);

    if (global) {
        rollbar.global(global);
    }

    return rollbar;
}

export const createTransport: createRollbarTransporter = (
    environment: string,
    config: Rollbar.Configuration,
    global?: Partial<Rollbar.Configuration>,
): Transport => {
    const rollbar = getRollbarLogger(environment, config, global);

    return {
        name: 'rollbar',
        log: (
            level: LoggerLevel,
            payload: LogObject,
            context: LoggerContext,
            error: Error | null | undefined,
        ): void => {
            // if you have an error object - rollbar really wants that first so it can find the stack.
            if (error) {
                rollbar[level](error, context, payload);
            } else {
                rollbar[level](payload.message, formatRollbarCustomObject(payload, context));
            }
        },
    };
};
